/**
 * @module app.um
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { IAuthSearchFilters } from 'app/um/services/auth-service';
import { I18N } from 'aurelia-i18n';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { AuthService } from 'app/um/services/auth-service';
import { IDisplayOptions } from 'app/shared/models/essette-interfaces';
import * as UM from 'app/um/models/essette-api';
import * as Core from 'app/core/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

export interface IAuthSearchOptions {
    mine?: IDisplayOptions;
    openOnly?: IDisplayOptions;
    closedOnly?: IDisplayOptions;
}

@autoinject
export class AuthFilters extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public searchFilters: IAuthSearchFilters = {};

    @bindable.objectNull()
    public availableFilters: string[] | null = null;

    @bindable.stringNull()
    protected labelId: string | null = null;

    protected types: UM.AuthType[] = [];
    protected classes: UM.AuthClass[] = [];
    protected statuses: UM.AuthStatus[] = [];
    protected assigned: Core.User[] = [];
    protected providerTypes: CD.AuthProviderType[] = [];
    protected maxAuthNumberLength: number = 20;
    protected maxProviderLastNameLength: number = 50;

    ////****************** Element References ***************************************////


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly authService: AuthService,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        this.loadLookups();
    }

    ////****************** Events ***************************************************////
    protected statusChanged(): void {
        this.searchFilters.statusReason = null;
    }

    ////****************** Functions ************************************************////
    protected filterVisible(name: string): boolean {
        if(!this.availableFilters) { return true; }

        const index = this.availableFilters.indexOf(name);

        return index >= 0;
    }

    protected toggleMyAuthsOnly(value: boolean): void {
        this.searchFilters.mine = value;

        if(value) {
            const user: Core.User | undefined = this.assigned.find(a => a.id === this.env.user.id);
            this.searchFilters.assignedTo = user;
        }
        else {
            this.searchFilters.assignedTo = undefined;
        }
    }

    protected toggleOpenAuthsOnly(value: boolean): void {
        this.searchFilters.openOnly = value;

        if(value) {
            this.searchFilters.status = undefined;
            this.searchFilters.statusReason = null;
        }
    }

    protected toggleClosedAuthsOnly(value: boolean): void {
        this.searchFilters.closedOnly = value;

        if(value) {
            this.searchFilters.status = undefined;
            this.searchFilters.statusReason = null;
        }
    }

    private async loadLookups(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const typeFields = ['description'];
        const classIncludes = ['subClasses'];
        const classFields = ['description', 'subClasses.description'];
        const statusIncludes = ['reasons'];
        const statusFields = ['description', 'reasons.description'];
        const userFields = ['description', 'openAuthCount'];
        const providerTypeFields = ['description'];

        try {
            this.state.beginLoad();
            const [authTypes, authClasses, authStatuses, users, providerTypes] = await Promise.all(
                [
                    this.filterVisible('type') ? this.authService.loadAuthTypes(true, options, undefined, typeFields) : null,
                    this.filterVisible('class') ? this.authService.loadAuthClasses(options, classIncludes, false, classFields) : null,
                    this.filterVisible('status') ? this.authService.loadAuthStatuses(options, statusIncludes, undefined, statusFields) : null,
                    this.filterVisible('assignedTo') ? this.authService.loadAuthOwners(options, undefined, undefined, undefined, userFields) : null,
                    this.filterVisible('providerType') ? this.authService.loadAuthProviderTypes(options, undefined, providerTypeFields) : null
                ]
            );

            this.types = authTypes?.results || [];
            this.classes = authClasses?.results || [];
            this.statuses = authStatuses?.results || [];
            this.assigned = users?.results || [];
            this.providerTypes = providerTypes?.results || [];

            // add empty records to represent unassigned values
            this.assigned.unshift({
                id: '',
                $display: `<${this.i18n.tr('um:auth.assignedTo.none')}>`
            });

            if(this.searchFilters.mine && this.env.user) {
                this.searchFilters.assignedTo = { id: this.env.user.id }
            }
        } catch(error) {
            this.logger.error('Error Loading Lookups', error);
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    protected classChange(): void {
        this.searchFilters.subClass = null;
    }

    ////****************** Service Calls ********************************************////

}
